<template>
  <b-card>
    <b-card class="multi-card">
      <div slot="header">
        <b-row>
          <b-col md="6">
            <h5>{{ $t('CONFIGURED.ROLE_ORDER') }}</h5>
          </b-col>
          <b-col md="6">
            <div class="float-right add-field-btn">
              <b-button type="submit" size="sm" variant="secondary" class="mt-0" v-b-modal.newRole @click="clearRoleModal">
                <i class="fa fa-plus"> </i> <span> {{ $t('CONFIGURED.ADD_ROLE') }} </span>
              </b-button>
              <b-button type="submit" variant="secondary" size="sm" class="mt-0" @click="updateRoleOrder"><i class="fa fa-save"></i> {{ $t('COMMON.SAVE') }}</b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <div class="timeline-div">
        <ul class="timeline timeline-horizontal">
        <draggable v-model="selectedRoles" :options="{ group:'roles'}" class="flex-container">
          <li class="timeline-item" v-for="(role, index) in selectedRoles" :key="index">
            <div class="timeline-badge bg-primary"><strong>{{index + 1}}</strong></div>
            <div class="timeline-panel role-panel"><p>{{ role['name'] | titlize }}</p>
              <b-dropdown no-caret class="panel-option">
                <template slot="button-content">
                  <div><i class="fa fa-ellipsis-v"></i></div>
                </template>
                <b-dropdown-item v-b-modal.editRole @click="setEditRole(role)">{{ $t('COMMON.EDIT') }}</b-dropdown-item>
                <b-dropdown-item v-b-modal.deleteRole @click="setDeleteRole(role)">{{ $t('COMMON.DELETE') }}</b-dropdown-item>
              </b-dropdown>
            </div>
          </li>
          </draggable>
        </ul>
      </div>
      <em class="italic">{{ $t('CONFIGURED.ROLE_NOTE') }}</em>
      <b-modal hide-header-close id="newRole" :title="$t('CONFIGURED.NEW_ROLE')" header-bg-variant="light" ref="newRoleModal" centered>
        <b-card class="mb-0 p-3">
          <b-row class="form-group">
            <b-col cols="12">
              <b-form-group :label="$t('CONFIGURED.ROLE_NAME')" label-for="roleName">
                <b-form-input class="rolename" id="roleName"
                  type="text" :placeholder="$t('CONFIGURED.ROLE_NAME')"
                  :class="{'is-invalid': $v.newRoleName.$error, 'is-valid': !$v.newRoleName.$invalid}"
                  v-model="newRoleName"
                  @input="$v.newRoleName.$touch()">
                </b-form-input>
                <b-form-invalid-feedback v-if="!$v.newRoleName.required">Role Name can't be blank</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="!$v.newRoleName.valid">Role Name can't accept symbols</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="!$v.newRoleName.minLength || !$v.newRoleName.maxLength">Role Name length should be more than 3 and less than 15 characters</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="!$v.newRoleName.isUnique">Either role is already registered or not allowed.</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <div slot="modal-footer">
          <b-btn size="sm" variant="secondary" @click="createRole"> {{ $t('CONFIGURED.CREATE') }} </b-btn>
          <b-btn size="sm" variant="light" @click="hideCreateRoleModal"> {{ $t('COMMON.CLOSE') }} </b-btn>
        </div>
      </b-modal>
      <b-modal hide-header-close id="deleteRole" title="Role Delete Confirmation" header-bg-variant="light" ref="deleteRoleModal" centered>
        <b-card class="mb-0">
          <b-row class="form-group">
            <b-col cols="12">
              <p>Are you sure you want to delete the role '<strong class="text-capitalize">{{ selectedRole.name }}</strong>' ?</p>
              <p>This may affect the forms and users under this role.</p>
            </b-col>
          </b-row>
        </b-card>
        <div slot="modal-footer">
          <b-btn size="sm" variant="danger" @click="deleteRole"> Delete </b-btn>
          <b-btn size="sm" variant="light" @click="hideDeleteRoleModal"> {{ $t('COMMON.CLOSE') }} </b-btn>
        </div>
      </b-modal>
      <b-modal hide-header-close id="editRole" title="Edit Role Name" header-bg-variant="light" ref="editRoleModal" centered>
        <b-card class="mb-0 p-3">
          <b-row class="form-group">
            <b-col cols="12">
              <b-form-group :label="$t('CONFIGURED.ROLE_NAME')" label-for="roleName">
                <b-form-input  class="rolename" id="roleName"
                  type="text" :placeholder="$t('CONFIGURED.ROLE_NAME')"
                  :class="{'is-invalid': $v.newRoleName.$error, 'is-valid': !$v.newRoleName.$invalid}"
                  v-model="newRoleName"
                  @input="$v.newRoleName.$touch()">
                </b-form-input>
                <b-form-invalid-feedback v-if="!$v.newRoleName.required">Role Name can't be blank</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="!$v.newRoleName.valid">Role Name can't accept symbols</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="!$v.newRoleName.minLength || !$v.newRoleName.maxLength">Role Name length should be more than 3 and less than 15 characters</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="!$v.newRoleName.isUnique">Either role is already registered or not allowed.</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <div slot="modal-footer">
          <b-btn size="sm" variant="secondary" @click="editRole" :disabled="$v.newRoleName.$error"> Change </b-btn>
          <b-btn size="sm" variant="light" @click="hideEditRoleModal"> {{ $t('COMMON.CLOSE') }} </b-btn>
        </div>
      </b-modal>
    </b-card>
  </b-card>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { required, maxLength, minLength } from 'vuelidate/lib/validators'
import _ from 'lodash'

export default {
  data () {
    return {
      selectedRoles: [],
      selectedRole: '',
      newRoleName: ''
    }
  },
  computed: {
    ...mapGetters(['idToken'])
  },
  components: {
    draggable
  },
  created () {
    this.selectedRoles = this.$store.state.selectedRoles
  },
  methods: {
    clearRoleModal () {
      this.newRoleName = ''
      this.$nextTick(() => { this.$v.$reset() })
    },
    hideCreateRoleModal () {
      this.newRoleName = ''
      this.$refs.newRoleModal.hide()
    },
    setEditRole (role) {
      this.selectedRole = role
      this.newRoleName = this.$options.filters.titlize(role['name'])
    },
    hideEditRoleModal () {
      this.selectedRole = ''
      this.newRoleName = ''
      this.$refs.editRoleModal.hide()
    },
    setDeleteRole (role) {
      this.selectedRole = role
    },
    hideDeleteRoleModal () {
      this.selectedRole = ''
      this.$refs.deleteRoleModal.hide()
    },
    updateRoleOrder () {
      var formData = new window.FormData()
      formData.append('selected_roles', JSON.stringify(this.selectedRoles))
      Vue.axios.post('/settings/account_roles/update_roles', formData, { headers: { Authorization: `Bearer ${this.idToken}` } })
        .then(response => {
          Vue.toasted.show(this.$t('CONFIGURED.ROLE_SUCCESS'))
          this.updateRoleAndOrder(response.data.selected_roles, response.data.user_role_order)
        }, error => {
          if (error.response.status === 401) {
            Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
            console.log(this.$t('COMMON.MSG_EXPIRED'))
          } else {
            Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
          }
        })
    },
    createRole () {
      if (this.newRoleName.trim() === '') {
        this.$v.$touch()
      } else if (!this.$v.$invalid) {
        let updateData = new window.FormData()
        updateData.append('role', this.newRoleName)
        Vue.axios.post('/settings/account_roles/create_role', updateData, { headers: { Authorization: `Bearer ${this.idToken}` } })
          .then(response => {
            this.updateRoleAndOrder(response.data.selected_roles, response.data.user_role_order)
            Vue.toasted.show(this.newRoleName + this.$t('CONFIGURED.ROLE_CREATED'))
            this.hideCreateRoleModal()
          }, error => {
            if (error.status === 401) {
              Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
              console.log(this.$t('COMMON.MSG_EXPIRED'))
            } else if (error.status === 400) {
              Vue.toasted.show(error.data.message, { icon: 'chain-broken', type: 'error' })
            } else {
              Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
            }
          })
      }
    },
    editRole () {
      let updateData = new window.FormData()
      updateData.append('new_role_name', this.newRoleName)
      updateData.append('role', JSON.stringify(this.selectedRole))
      Vue.axios.post('/settings/account_roles/edit_role', updateData, { headers: { Authorization: `Bearer ${this.idToken}` } })
        .then(response => {
          if (response.data.success) {
            this.updateRoleAndOrder(response.data.selected_roles, response.data.user_role_order)
            Vue.toasted.show(this.selectedRole.name + ' is changed to ' + this.newRoleName)
            this.hideEditRoleModal()
          } else {
            Vue.toasted.show(response.data.msg, { icon: 'chain-broken', type: 'error' })
          }
        }, error => {
          if (error.response.status === 401) {
            Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
            console.log(this.$t('COMMON.MSG_EXPIRED'))
          } else {
            Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
          }
        })
    },
    deleteRole () {
      let updateData = new window.FormData()
      updateData.append('role', JSON.stringify(this.selectedRole))
      Vue.axios.post('/settings/account_roles/delete_role', updateData, { headers: { Authorization: `Bearer ${this.idToken}` } })
        .then(response => {
          if (response.data.success) {
            this.updateRoleAndOrder(response.data.selected_roles, response.data.user_role_order)
            Vue.toasted.show(this.selectedRole.name + ' is deleted')
            this.hideDeleteRoleModal()
          } else {
            Vue.toasted.show(response.data.msg, { icon: 'chain-broken', type: 'error' })
          }
        }, error => {
          if (error.response.status === 401) {
            Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
            console.log(this.$t('COMMON.MSG_EXPIRED'))
          } else {
            Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
          }
        })
    },
    updateRoleAndOrder (selectedRoles, userRoleOrder) {
      this.selectedRoles = selectedRoles
      this.$store.commit('setSelectedRoles', selectedRoles)
      this.$store.commit('setUserRoleOrder', userRoleOrder)
      window.localStorage.setItem('userRoleOrder', userRoleOrder)
      window.localStorage.setItem('selectedRoles', JSON.stringify(selectedRoles))
    }
  },
  validations: {
    newRoleName: {
      required,
      maxLength: maxLength(15),
      minLength: minLength(3),
      valid: function (value) {
        const regexExp = /^[a-zA-Z0-9 _-]*$/.test(value)
        return regexExp
      },
      isUnique (value, vm) {
        value = value.trim().replace(/\s+/g, ' ').toLowerCase()
        if (!value || this.selectedRole.name === value) return true
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            let selectedRoles = vm.selectedRoles.slice().concat({ 'name': 'admin' }, { 'name': 'super admin' })
            resolve(!_.some(selectedRoles, {'name': value}))
          }, 350 + Math.random() * 300)
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .card {
    background: #ebeff3;
    border-radius: 10px;
  }
  .card-header {
    background: #f7f7f7;
  }
  .timeline-div {
    display: inline-block;
    width: 100%;
    overflow-y: auto;
    background-color: #f7f7f7;
    padding-top: 35px;
    margin: 0 auto;
    margin-bottom: 10px;
  }
  .timeline,
  .timeline-horizontal {
    list-style: none;
    padding: 20px;
    position: relative;
  }
  .timeline:before {
    top: 40px;
    bottom: 0;
    position: absolute;
    content: " ";
    width: 5px;
    background-color: #5b648f;
    margin-left: -1.5px;
  }
  .timeline .timeline-item {
    margin-bottom: 20px;
    position: relative;
  }
  .timeline .timeline-item:before,
  .timeline .timeline-item:after {
    content: "";
    display: table;
  }
  .timeline .timeline-item:after {
    clear: both;
  }
  .timeline .timeline-item .timeline-badge {
    color: #fff;
    width: 60px;
    height: 60px;
    line-height: 52px;
    font-size: 18px;
    text-align: center;
    position: absolute;
    top: 18px;
    left: 50%;
    margin-left: -25px;
    background-color: #7c7c7c;
    border: 5px solid #5b648f;
    z-index: 100;
    border-top-right-radius: 50%;
    border-top-left-radius: 50%;
    border-bottom-right-radius: 50%;
    border-bottom-left-radius: 50%;
    cursor: move;
  }
  .timeline .timeline-item .timeline-badge.primary {
    background-color: #1f9eba;
  }
  .timeline .timeline-item .timeline-panel {
    position: relative;
    width: 20%;
    float: left;
    right: 10px;
    border: 1px solid white;
    background: #ffffff;
    border-radius: 10px;
    cursor: move;
    padding: 5px;
    max-width: 15em;
  }
  .timeline .timeline-item .timeline-panel:before {
    position: absolute;
    top: 26px;
    right: -16px;
    display: inline-block;
    border-top: 16px solid transparent;
    border-left: 16px solid #fff;
    border-right: 0 solid #fff;
    border-bottom: 16px solid transparent;
    content: " ";
  }
  .timeline .timeline-item .timeline-panel p {
      margin: 4px;
      text-align: center;
      font-weight: bolder;
  }
  .timeline-horizontal {
    list-style: none;
    position: relative;
    padding: 20px 0px 20px 0px;
    display: table;
    width: 100%;
  }
  .timeline-horizontal:before {
    height: 5px;
    top: auto;
    bottom: 26px;
    right: 0;
    width: 100%;
    margin-bottom: 20px;
  }
  .timeline-horizontal .timeline-item {
    display: table-cell;
    min-width: 200px;
    float: none !important;
    padding-left: 10px;
    padding-right: 10px;
    margin: 0 auto;
    vertical-align: bottom;
  }
  .timeline-horizontal .timeline-item .timeline-panel {
    top: auto;
    bottom: 36px;
    display: inline-block;
    float: none !important;
    left: 0 !important;
    right: 0 !important;
    width: 100%;
    margin-bottom: 20px;
  }
  .timeline-horizontal .timeline-item .timeline-panel:before {
    top: auto;
    bottom: -16px;
    left: 76px !important;
    right: auto;
    border-right: 16px solid transparent !important;
    border-top: 16px solid #fff !important;
    border-bottom: 0 solid #fff !important;
    border-left: 16px solid transparent !important;
  }
  .timeline-horizontal .timeline-item:before,
  .timeline-horizontal .timeline-item:after {
    display: none;
  }
  .timeline-horizontal .timeline-item .timeline-badge {
    top: auto;
    bottom: 0px;
  }
  ul li.timeline-item{
   max-width: 2em;
  }
  .multi-card .card-body{
    padding: 1.25em 0 0 0;
  }
  .multi-card{
    margin-bottom:0;
  }
  .flex-container {
    display: flex;
    justify-content: center;
  }

  .timeline {
    margin: 0;
  }
  .button-style {
    font-size: 1rem;
    padding-left: 2.5rem;
    padding-right: 2.5rem;
    border-radius: 16px;
  }

  .button-style:nth-child(1) {
    margin-right: .5rem;
  }

  .button-style:nth-child(2) {
    margin-left: .5rem;
  }

  .panel-option:hover {
    cursor: pointer;
  }

  .panel-option {
    display: inline;
    position: absolute;
    right: 0;
    padding: 0 0.2rem;
  }

  .timeline-item .timeline-panel.role-panel {
    display: flex;
    justify-content: center;
  }
</style>
<style>
  .panel-option button {
    color: red;
    background-color: white;
  }
  .panel-option button ,.panel-option button:active:focus,
  .panel-option button:hover, .panel-option button:active,
  .panel-option .btn:not(:disabled):not(.disabled):active{
    color: inherit;
    background-color: inherit;
    border: none;
    box-shadow: none;
    padding: 5px 6px;
  }
</style>


// WEBPACK FOOTER //
// src/components/settings/roleOrder.vue